import React from 'react';
import { Product } from '@/types';
import { ExternalLink, Tag, DollarSign } from 'lucide-react';
import { clsx } from 'clsx';

interface ProductCardProps {
  product: Product;
  onClick?: (product: Product) => void;
  isSelected?: boolean;
  variant?: 'default' | 'sidebar' | 'compact';
}

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onClick,
  isSelected = false,
  variant = 'default',
}) => {
  const handleClick = () => {
    onClick?.(product);
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (product.link) {
      window.open(product.link, '_blank', 'noopener,noreferrer');
    }
  };

  const getPlaceholderImage = (width = 300, height = 200) => {
    const productName = product.name || 'Product';
    const labels = product.labels || [];
    const category = product.category || '';
    
    // Create a seed from product name for consistent images
    const _seed = Math.abs(productName.split('').reduce((a, b) => ((a << 5) - a) + b.charCodeAt(0), 0)) % 1000;
    
    // Determine category for color scheme
    const labelsStr = labels.join(' ').toLowerCase();
    const categoryStr = category.toLowerCase();
    const combined = `${labelsStr} ${categoryStr} ${productName.toLowerCase()}`;
    
    let bgColor = '6366f1'; // Default purple
    let icon = '🎁'; // Default gift icon
    
    if (combined.includes('drinkware') || combined.includes('tumbler') || combined.includes('mug')) {
      bgColor = '3b82f6'; // Blue
      icon = '☕';
    } else if (combined.includes('bag') || combined.includes('tote')) {
      bgColor = '10b981'; // Green
      icon = '👜';
    } else if (combined.includes('premium') || combined.includes('luxury')) {
      bgColor = '8b5cf6'; // Purple
      icon = '⭐';
    } else if (combined.includes('tech') || combined.includes('electronic')) {
      bgColor = 'f59e0b'; // Orange
      icon = '📱';
    }
    
    // Use a simple, reliable placeholder with proper URL encoding
    const safeName = productName.substring(0, 15).replace(/[^a-zA-Z0-9 ]/g, '');
    return `https://via.placeholder.com/${width}x${height}/${bgColor}/ffffff.png?text=${encodeURIComponent(icon + ' ' + safeName)}`;
  };

  const getImageSrc = () => {
    // Prioritize actual images over placeholder photo field
    let imageUrl = null;

    // Check images array first (contains real product images)
    if (Array.isArray(product.images) && product.images.length > 0) {
      imageUrl = product.images[0];
    } else if (typeof product.images === 'string' && product.images) {
      imageUrl = product.images;
    } else if (typeof product.images === 'object' && product.images !== null) {
      const firstImage = Object.values(product.images)[0];
      imageUrl = firstImage;
    } else if (product.photo && !product.photo.includes('via.placeholder.com')) {
      // Only use photo if it's not a placeholder
      imageUrl = product.photo;
    }

    // Return placeholder with product name if no real image found
    if (!imageUrl) {
      return getPlaceholderImage();
    }

    return imageUrl;
  };

  const getPrimaryPrice = () => {
    if (product.price_display) {
      return product.price_display;
    }
    return product.sale_price || product.original_price || 'Price on request';
  };

  const hasDiscount = product.has_discount || (product.sale_price && product.original_price && product.sale_price !== product.original_price);

  // Compact variant for chat messages
  if (variant === 'compact') {
    return (
      <div
        className={clsx(
          'bg-white rounded-lg border border-gray-200 overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-md',
          isSelected && 'ring-2 ring-purple-500'
        )}
        onClick={handleClick}
      >
        <div className="flex">
          <div className="w-20 h-20 flex-shrink-0">
            <img
              src={getImageSrc()}
              alt={product.name || 'Product'}
              className="w-full h-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = getPlaceholderImage(80, 80);
              }}
            />
          </div>
          <div className="flex-1 p-3">
            <h4 className="font-medium text-sm text-gray-900 line-clamp-2">
              {product.name || 'Unnamed Product'}
            </h4>
            <div className="flex items-center justify-between mt-1">
              <span className="text-purple-600 font-semibold text-sm">
                {getPrimaryPrice()}
              </span>
              {product.link && (
                <button
                  onClick={handleLinkClick}
                  className="text-gray-400 hover:text-purple-600 transition-colors"
                  title="View details"
                >
                  <ExternalLink size={14} />
                </button>
              )}
            </div>
            {hasDiscount && (
              <span className="inline-block bg-red-100 text-red-800 text-xs px-2 py-1 rounded mt-1">
                Sale
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Sidebar variant
  if (variant === 'sidebar') {
    return (
      <div
        className={clsx(
          'bg-white rounded-lg border border-gray-200 overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-md',
          isSelected && 'ring-2 ring-purple-500'
        )}
        onClick={handleClick}
      >
        <div className="aspect-square">
          <img
            src={getImageSrc()}
            alt={product.name || 'Product'}
            className="w-full h-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = getPlaceholderImage(300, 200);
            }}
          />
        </div>
        <div className="p-3">
          <h4 className="font-medium text-sm text-gray-900 line-clamp-2 mb-1">
            {product.name || 'Unnamed Product'}
          </h4>
          <div className="flex items-center justify-between mb-2">
            <span className="text-purple-600 font-semibold text-sm">
              {getPrimaryPrice()}
            </span>
            {product.link && (
              <button
                onClick={handleLinkClick}
                className="text-gray-400 hover:text-purple-600 transition-colors"
                title="View details"
              >
                <ExternalLink size={14} />
              </button>
            )}
          </div>
          {hasDiscount && product.discount_badge && (
            <span className="inline-block bg-red-100 text-red-800 text-xs px-2 py-1 rounded">
              {product.discount_badge}
            </span>
          )}
        </div>
      </div>
    );
  }

  // Default variant
  return (
    <div
      className={clsx(
        'bg-white rounded-xl border border-gray-200 overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-1',
        isSelected && 'ring-2 ring-purple-500'
      )}
      onClick={handleClick}
    >
      <div className="relative">
        <div className="aspect-[4/3]">
          <img
            src={getImageSrc()}
            alt={product.name || 'Product'}
            className="w-full h-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = getPlaceholderImage(300, 200);
            }}
          />
        </div>
        
        {/* Discount badge */}
        {hasDiscount && product.discount_badge && (
          <div className="absolute top-3 left-3">
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
              {product.discount_badge}
            </span>
          </div>
        )}
        
        {/* Bulk discount indicator */}
        {product.has_bulk_discount && (
          <div className="absolute top-3 right-3">
            <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">
              BULK
            </span>
          </div>
        )}
      </div>

      <div className="p-4">
        <h3 className="font-semibold text-gray-900 line-clamp-2 mb-2">
          {product.name || 'Unnamed Product'}
        </h3>
        
        {/* Price */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <DollarSign size={16} className="text-purple-600" />
            <span className="text-purple-600 font-bold">
              {getPrimaryPrice()}
            </span>
          </div>
          
          {product.link && (
            <button
              onClick={handleLinkClick}
              className="flex items-center space-x-1 text-gray-600 hover:text-purple-600 transition-colors"
              title="View details"
            >
              <ExternalLink size={16} />
              <span className="text-sm">Details</span>
            </button>
          )}
        </div>

        {/* Description */}
        {product.description && (
          <p className="text-gray-600 text-sm line-clamp-3 mb-3">
            {product.description}
          </p>
        )}

        {/* Category and material tags */}
        <div className="flex flex-wrap gap-2">
          {product.category && (
            <span className="inline-flex items-center space-x-1 bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded">
              <Tag size={12} />
              <span>{product.category}</span>
            </span>
          )}
          {product.material && (
            <span className="inline-flex items-center bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded">
              {product.material}
            </span>
          )}
          {product.brand && (
            <span className="inline-flex items-center bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded">
              {product.brand}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};